// src/pages/dashboard/Leave.js
import React, { useState, useEffect } from 'react';
import {
  Card,
  Button,
  Table,
  Space,
  Modal,
  Form,
  Input,
  DatePicker,
  Select,
  message,
  Row,
  Col,
  Radio,
  Tooltip,
  Popconfirm,
  Switch
} from 'antd';
import {
  PlusOutlined,
  CalendarOutlined,
  ClockCircleOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  CheckOutlined,
  CloseOutlined,
  DeleteOutlined,
  MessageOutlined,
  StopOutlined
} from '@ant-design/icons';
import { supabase } from '../../services/supabase';
import dayjs from 'dayjs';
import { Page, PageHeader, StatGrid, StatCard, StatusPill, EmptyState } from '../../components/ui';

const LEAVE_TYPE_LABELS = { sick: 'Sick', local: 'Local', unpaid: 'Unpaid', other: 'Other' };
const LEAVE_TYPE_TONES = { sick: 'warning', local: 'primary', unpaid: 'danger', other: 'neutral' };
const LEAVE_STATUS_TONES = { approved: 'positive', rejected: 'danger', cancelled: 'neutral', pending: 'warning' };

const { RangePicker } = DatePicker;
const { Option } = Select;
const { TextArea } = Input;

const Leaves = () => {
  const [loading, setLoading] = useState(false);
  const [leaves, setLeaves] = useState([]);
  const [visible, setVisible] = useState(false);
  const [form] = Form.useForm();
  const [companyId, setCompanyId] = useState(null);
  const [commentVisible, setCommentVisible] = useState(false);
  const [selectedLeave, setSelectedLeave] = useState(null);
  const [commentForm] = Form.useForm();
  const [employees, setEmployees] = useState([]);
  const [leaveType, setLeaveType] = useState('full_day');
  const [actionLoading, setActionLoading] = useState({});
  const [showCancelled, setShowCancelled] = useState(false);

  const showCommentModal = (record) => {
    setSelectedLeave(record);
    setCommentVisible(true);
    commentForm.setFieldsValue({
      comments: record.comments || ''
    });
  };

  const fetchEmployees = async (company_id) => {
    try {
      const { data, error } = await supabase
        .from('employees')
        .select(`
          id,
          first_name,
          last_name,
          department,
          employee_number,
          status
        `)
        .eq('company_id', company_id)
        .eq('status', 'active')
        .order('first_name');

      if (error) throw error;
      setEmployees(data || []);
    } catch (error) {
      message.error('Error fetching employees');
      console.error(error);
    }
  };

  useEffect(() => {
    fetchCompanyId();
  }, []);

  const fetchCompanyId = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      const { data, error } = await supabase
        .from('companies')
        .select('id')
        .eq('user_id', user.id)
        .single();

      if (error) throw error;
      setCompanyId(data.id);
      fetchLeaves(data.id);
      fetchEmployees(data.id);
    } catch (error) {
      message.error('Error fetching company data');
      console.error(error);
    }
  };

  const fetchLeaves = async (company_id) => {
    try {
      setLoading(true);
      const { data, error } = await supabase
        .from('leaves')
        .select(`
          *,
          employee:employee_id (
            id,
            first_name,
            last_name,
            department
          ),
          approver:approved_by (
            id,
            first_name,
            last_name
          )
        `)
        .eq('company_id', company_id)
        .order('start_date', { ascending: false });

      if (error) throw error;
      setLeaves(data || []);
    } catch (error) {
      message.error('Error fetching leave data');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (values) => {
    try {
      setLoading(true);
      const [startDate, endDate] = values.date_range;

      const leaveData = {
        company_id: companyId,
        employee_id: values.employee_id,
        type: values.type,
        start_date: startDate.format('YYYY-MM-DD'),
        end_date: endDate.format('YYYY-MM-DD'),
        reason: values.reason,
        status: 'pending',
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
        leave_duration: values.leave_duration || 'full_day',
        half_day_part: values.leave_duration === 'half_day' ? values.half_day_part : null
      };

      const { error } = await supabase
        .from('leaves')
        .insert([leaveData]);

      if (error) throw error;

      message.success('Leave request submitted successfully');
      form.resetFields();
      setVisible(false);
      fetchLeaves(companyId);
    } catch (error) {
      message.error('Error submitting leave request');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  // Direct approve function
  const handleDirectApprove = async (leaveId) => {
    try {
      setActionLoading(prev => ({ ...prev, [`approve_${leaveId}`]: true }));

      const { data: { user }, error: userError } = await supabase.auth.getUser();
      if (userError) throw userError;

      const { data: profileData, error: profileError } = await supabase
        .from('profiles')
        .select('*')
        .eq('id', user.id)
        .single();

      if (profileError) throw new Error('Could not fetch profile data');

      const { data: employeeData, error: employeeError } = await supabase
        .from('employees')
        .select('*')
        .eq('company_id', companyId)
        .eq('first_name', user.user_metadata?.display_name || profileData?.first_name)
        .single();

      if (employeeError) throw new Error('Could not find employee record');

      const updateData = {
        status: 'approved',
        approval_date: new Date().toISOString(),
        approved_by: employeeData.id,
        updated_at: new Date().toISOString()
      };

      const { error } = await supabase
        .from('leaves')
        .update(updateData)
        .eq('id', leaveId)
        .eq('company_id', companyId);

      if (error) throw error;

      message.success('Leave request approved');
      fetchLeaves(companyId);
    } catch (error) {
      console.error('Error approving leave:', error);
      message.error('Error approving leave: ' + (error.message || 'Unknown error'));
    } finally {
      setActionLoading(prev => ({ ...prev, [`approve_${leaveId}`]: false }));
    }
  };

  // Direct reject function
  const handleDirectReject = async (leaveId) => {
    try {
      setActionLoading(prev => ({ ...prev, [`reject_${leaveId}`]: true }));

      const { data: { user }, error: userError } = await supabase.auth.getUser();
      if (userError) throw userError;

      const { data: profileData, error: profileError } = await supabase
        .from('profiles')
        .select('*')
        .eq('id', user.id)
        .single();

      if (profileError) throw new Error('Could not fetch profile data');

      const { data: employeeData, error: employeeError } = await supabase
        .from('employees')
        .select('*')
        .eq('company_id', companyId)
        .eq('first_name', user.user_metadata?.display_name || profileData?.first_name)
        .single();

      if (employeeError) throw new Error('Could not find employee record');

      const updateData = {
        status: 'rejected',
        approval_date: new Date().toISOString(),
        approved_by: employeeData.id,
        updated_at: new Date().toISOString()
      };

      const { error } = await supabase
        .from('leaves')
        .update(updateData)
        .eq('id', leaveId)
        .eq('company_id', companyId);

      if (error) throw error;

      message.success('Leave request rejected');
      fetchLeaves(companyId);
    } catch (error) {
      console.error('Error rejecting leave:', error);
      message.error('Error rejecting leave: ' + (error.message || 'Unknown error'));
    } finally {
      setActionLoading(prev => ({ ...prev, [`reject_${leaveId}`]: false }));
    }
  };

  // Update comments function
  const handleUpdateComments = async (values) => {
    try {
      setLoading(true);

      const { error } = await supabase
        .from('leaves')
        .update({
          comments: values.comments,
          updated_at: new Date().toISOString()
        })
        .eq('id', selectedLeave.id)
        .eq('company_id', companyId);

      if (error) throw error;

      message.success('Comments updated successfully');
      setCommentVisible(false);
      commentForm.resetFields();
      setSelectedLeave(null);
      fetchLeaves(companyId);
    } catch (error) {
      console.error('Error updating comments:', error);
      message.error('Error updating comments');
    } finally {
      setLoading(false);
    }
  };

  // Delete leave request function
  const handleDeleteLeave = async (leaveId) => {
    try {
      setActionLoading(prev => ({ ...prev, [`delete_${leaveId}`]: true }));

      console.log('Attempting to delete leave:', { leaveId, companyId });

      // First check if the record exists and get full details
      const { data: existingRecord, error: checkError } = await supabase
        .from('leaves')
        .select('*')
        .eq('id', leaveId)
        .single();

      console.log('Existing record check:', { existingRecord, checkError });

      if (checkError) {
        throw new Error('Error checking record: ' + checkError.message);
      }

      if (!existingRecord) {
        message.warning('Leave request not found');
        fetchLeaves(companyId);
        return;
      }

      // Check if this leave is referenced in payroll records (common cause of delete failure)
      const { data: payrollRefs, error: payrollError } = await supabase
        .from('payroll_records')
        .select('id')
        .eq('company_id', companyId)
        .limit(1);

      console.log('Checking for potential referential constraints...');

      // Try the delete with proper RLS context
      const { data, error } = await supabase
        .from('leaves')
        .delete()
        .eq('id', leaveId)
        .select();

      console.log('Delete response:', { data, error });

      if (error) {
        console.error('Delete error:', error);

        // Check for specific error types
        if (error.code === '23503') {
          throw new Error('Cannot delete: This leave request is referenced by other records (e.g., payroll). Please contact your administrator.');
        } else if (error.code === '42501') {
          throw new Error('Permission denied: You do not have permission to delete leave requests.');
        } else {
          throw new Error('Delete failed: ' + error.message);
        }
      }

      if (!data || data.length === 0) {
        console.warn('No records were deleted - likely RLS policy blocking deletion');

        // Instead of hard delete, try soft delete by updating status
        console.log('Attempting soft delete (status update to cancelled)...');
        const { data: softDeleteData, error: softDeleteError } = await supabase
          .from('leaves')
          .update({
            status: 'cancelled',
            updated_at: new Date().toISOString(),
            comments: (existingRecord.comments || '') + '\n[DELETED by user on ' + new Date().toLocaleString() + ']'
          })
          .eq('id', leaveId)
          .select();

        console.log('Soft delete response:', { softDeleteData, softDeleteError });

        if (softDeleteError || !softDeleteData || softDeleteData.length === 0) {
          throw new Error('Cannot delete or cancel this leave request. This may be due to database policies or the record being used elsewhere in the system.');
        }

        message.success('Leave request cancelled (marked as deleted)');
        fetchLeaves(companyId);
        return;
      }

      console.log('Successfully deleted leave request');
      message.success('Leave request deleted successfully');
      fetchLeaves(companyId);

    } catch (error) {
      console.error('Error deleting leave:', error);
      message.error(error.message || 'Error deleting leave request');
    } finally {
      setActionLoading(prev => ({ ...prev, [`delete_${leaveId}`]: false }));
    }
  };

  // Function to calculate leave days accounting for half-days
  const calculateLeaveDays = (record) => {
    const start = dayjs(record.start_date);
    const end = dayjs(record.end_date);
    const fullDays = end.diff(start, 'day') + 1;

    if (record.leave_duration === 'half_day' && fullDays === 1) {
      return 0.5;
    }

    if (record.leave_duration === 'half_day' && fullDays > 1) {
      return fullDays - 1 + 0.5;
    }

    return fullDays;
  };

  const columns = [
    {
      title: 'Employee',
      key: 'employee',
      width: 200,
      render: (_, record) => (
        <span className="stack-cell">
          <span style={{ fontWeight: 600 }}>
            {`${record.employee?.first_name || ''} ${record.employee?.last_name || ''}`.trim() || 'Unknown'}
          </span>
          <span className="stack-cell__sub">{record.employee?.department || 'Unassigned'}</span>
        </span>
      ),
    },
    {
      title: 'Type',
      dataIndex: 'type',
      width: 120,
      render: (type) => (
        <StatusPill tone={LEAVE_TYPE_TONES[type] || 'neutral'}>
          {LEAVE_TYPE_LABELS[type] || type || 'Other'}
        </StatusPill>
      ),
    },
    {
      title: 'Dates',
      key: 'dates',
      width: 210,
      render: (_, record) => {
        const start = dayjs(record.start_date);
        const end = dayjs(record.end_date);
        const days = calculateLeaveDays(record);
        const range = start.isSame(end, 'day')
          ? start.format('ddd D MMM YYYY')
          : `${start.format('D MMM')} - ${end.format('D MMM YYYY')}`;
        return (
          <span className="stack-cell">
            <span className="num" style={{ fontWeight: 600 }}>{range}</span>
            <span className="stack-cell__sub">
              {days} {days === 1 ? 'day' : 'days'}
              {record.leave_duration === 'half_day' &&
                ` · ${record.half_day_part === 'morning' ? 'morning' : 'afternoon'}`}
            </span>
          </span>
        );
      },
      sorter: (a, b) => dayjs(a.start_date).unix() - dayjs(b.start_date).unix(),
    },
    {
      title: 'Status',
      dataIndex: 'status',
      width: 120,
      render: (status) => (
        <StatusPill tone={LEAVE_STATUS_TONES[status] || 'warning'}>
          {status ? status.charAt(0).toUpperCase() + status.slice(1) : 'Pending'}
        </StatusPill>
      ),
    },
    {
      title: 'Decision',
      key: 'approved_by',
      width: 170,
      render: (_, record) => record.approver ? (
        <span className="stack-cell">
          <span>{`${record.approver.first_name} ${record.approver.last_name}`}</span>
          <span className="stack-cell__sub">
            {record.approval_date ? dayjs(record.approval_date).format('D MMM YYYY, HH:mm') : ''}
          </span>
        </span>
      ) : <span className="text-muted">-</span>
    },
    {
      title: 'Comments',
      dataIndex: 'comments',
      width: 200,
      ellipsis: true,
      render: (comments) => comments
        ? <Tooltip title={comments}><span>{comments}</span></Tooltip>
        : <span className="text-muted">-</span>
    },
    {
      title: '',
      key: 'actions',
      width: 184,
      align: 'right',
      render: (_, record) => (
        <Space size={2}>
          <Tooltip title="Approve">
            <Button
              type="text"
              className="icon-action"
              data-tone="positive"
              aria-label="Approve leave"
              icon={<CheckOutlined />}
              loading={actionLoading[`approve_${record.id}`]}
              onClick={() => handleDirectApprove(record.id)}
            />
          </Tooltip>

          <Tooltip title="Reject">
            <Button
              type="text"
              className="icon-action"
              data-tone="danger"
              aria-label="Reject leave"
              icon={<CloseOutlined />}
              loading={actionLoading[`reject_${record.id}`]}
              onClick={() => handleDirectReject(record.id)}
            />
          </Tooltip>

          <Tooltip title="Add or edit comments">
            <Button
              type="text"
              className="icon-action"
              data-tone="primary"
              aria-label="Add or edit comments"
              icon={<MessageOutlined />}
              onClick={() => showCommentModal(record)}
            />
          </Tooltip>

          <Popconfirm
            title="Delete Leave Request"
            description="Are you sure you want to delete this leave request? This action cannot be undone."
            onConfirm={() => handleDeleteLeave(record.id)}
            okText="Yes, Delete"
            cancelText="Cancel"
            okButtonProps={{ danger: true }}
          >
            <Tooltip title="Delete request">
              <Button
                type="text"
                className="icon-action"
                data-tone="danger"
                aria-label="Delete leave request"
                icon={<DeleteOutlined />}
                loading={actionLoading[`delete_${record.id}`]}
              />
            </Tooltip>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  // Handle leave type change
  const handleLeaveTypeChange = (e) => {
    setLeaveType(e.target.value);
  };

  const countBy = (status) => leaves.filter(l => l.status === status).length;
  const visibleLeaves = showCancelled ? leaves : leaves.filter(leave => leave.status !== 'cancelled');

  return (
    <Page>
      <PageHeader
        title="Leave"
        subtitle="Leave requests, approvals and comments for every employee."
        actions={
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={() => setVisible(true)}
          >
            Apply for leave
          </Button>
        }
      />

      {/* Summary Statistics */}
      <StatGrid columns={5}>
        <StatCard
          label="Active requests"
          tone="primary"
          icon={<CalendarOutlined />}
          value={leaves.filter(l => l.status !== 'cancelled').length}
          caption="Everything except cancelled"
        />
        <StatCard
          label="Pending approval"
          tone="warning"
          icon={<ClockCircleOutlined />}
          value={countBy('pending')}
          caption={countBy('pending') ? 'Waiting for a decision' : 'Nothing waiting'}
        />
        <StatCard
          label="Approved"
          tone="positive"
          icon={<CheckCircleOutlined />}
          value={countBy('approved')}
        />
        <StatCard
          label="Rejected"
          tone="danger"
          icon={<CloseCircleOutlined />}
          value={countBy('rejected')}
        />
        <StatCard
          label="Cancelled"
          tone="neutral"
          icon={<StopOutlined />}
          value={countBy('cancelled')}
        />
      </StatGrid>

      <Card className="table-card">
        <div className="toolbar" style={{ padding: '12px 8px 8px' }}>
          <h2 className="section-title" style={{ paddingLeft: 4 }}>Leave requests</h2>
          <span className="toolbar__spacer" />
          <label className="text-muted" style={{ display: 'inline-flex', alignItems: 'center', gap: 8, fontSize: 13 }}>
            <Switch
              size="small"
              checked={showCancelled}
              onChange={setShowCancelled}
            />
            Show cancelled
          </label>
        </div>

        <Table
          columns={columns}
          dataSource={visibleLeaves}
          rowKey="id"
          loading={loading}
          scroll={{ x: 1100 }}
          locale={{
            emptyText: (
              <EmptyState
                plain
                icon={<CalendarOutlined />}
                title="No leave requests"
                description="Requests you add with Apply for leave appear here for approval."
              />
            )
          }}
        />
      </Card>

      {/* Apply for Leave Modal */}
      <Modal
        title="Apply for Leave"
        open={visible}
        onCancel={() => setVisible(false)}
        footer={null}
        width={700}
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={handleSubmit}
          initialValues={{ leave_duration: 'full_day' }}
        >
          <Form.Item
            name="employee_id"
            label="Employee"
            rules={[{ required: true, message: 'Please select an employee' }]}
          >
            <Select
              placeholder="Select employee"
              showSearch
              optionFilterProp="label"
              filterOption={(input, option) => {
                return option?.label?.toLowerCase().includes(input.toLowerCase());
              }}
              options={employees.map(emp => ({
                value: emp.id,
                label: `${emp.first_name} ${emp.last_name} - ${emp.employee_number || ''}`,
              }))}
            />
          </Form.Item>

          <Row gutter={16}>
            <Col xs={24} sm={12}>
              <Form.Item
                name="type"
                label="Leave Type"
                rules={[{ required: true }]}
              >
                <Select placeholder="Select leave type">
                  <Option value="sick">Sick Leave</Option>
                  <Option value="local">Local Leave</Option>
                  <Option value="unpaid">Unpaid Leave</Option>
                  <Option value="other">Other</Option>
                </Select>
              </Form.Item>
            </Col>
            <Col xs={24} sm={12}>
              <Form.Item
                name="date_range"
                label="Date Range"
                rules={[{ required: true }]}
              >
                <RangePicker style={{ width: '100%' }} />
              </Form.Item>
            </Col>
          </Row>

          <Form.Item
            name="leave_duration"
            label="Leave Duration"
            rules={[{ required: true }]}
          >
            <Radio.Group onChange={handleLeaveTypeChange}>
              <Radio value="full_day">Full Day</Radio>
              <Radio value="half_day">Half Day</Radio>
            </Radio.Group>
          </Form.Item>

          {leaveType === 'half_day' && (
            <Form.Item
              name="half_day_part"
              label="Half Day Part"
              rules={[{ required: leaveType === 'half_day', message: 'Please specify morning or afternoon' }]}
            >
              <Radio.Group>
                <Radio value="morning">Morning</Radio>
                <Radio value="afternoon">Afternoon</Radio>
              </Radio.Group>
            </Form.Item>
          )}

          <Form.Item
            name="reason"
            label="Reason"
            rules={[{ required: true }]}
          >
            <TextArea rows={4} />
          </Form.Item>

          <Form.Item style={{ marginBottom: 0, textAlign: 'right' }}>
            <Space>
              <Button onClick={() => setVisible(false)}>
                Cancel
              </Button>
              <Button type="primary" htmlType="submit" loading={loading}>
                Submit
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>

      {/* Comments Modal */}
      <Modal
        title="Add/Edit Comments"
        open={commentVisible}
        onCancel={() => {
          setCommentVisible(false);
          setSelectedLeave(null);
          commentForm.resetFields();
        }}
        footer={null}
      >
        <Form
          form={commentForm}
          layout="vertical"
          onFinish={handleUpdateComments}
        >
          <Form.Item
            name="comments"
            label="Comments"
            rules={[{ required: true, message: 'Please provide comments' }]}
          >
            <Input.TextArea
              rows={4}
              placeholder="Add your comments here..."
            />
          </Form.Item>

          <Form.Item style={{ marginBottom: 0, textAlign: 'right' }}>
            <Space>
              <Button onClick={() => {
                setCommentVisible(false);
                setSelectedLeave(null);
                commentForm.resetFields();
              }}>
                Cancel
              </Button>
              <Button type="primary" htmlType="submit" loading={loading}>
                Save Comments
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>
    </Page>
  );
};

export default Leaves;
